import React, { useContext, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../../shared/Sidebar";
import { LayoutContext } from "../Root";
import LayoutHeader from "./LayoutHeader";
import {
  BanknotesIcon,
  BriefcaseIcon,
  BuildingStorefrontIcon,
  HomeIcon,
  TruckIcon,
  DocumentArrowUpIcon,
  Cog8ToothIcon,
  ClipboardDocumentListIcon,
  RadioIcon,
  WrenchScrewdriverIcon,
  UsersIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import classNames from "classnames";
import useWindowSize from "../../hooks/useWindowSize";
import { useAuth } from "../../hooks/useAuth";
import Breadcrumbs from "../../shared/Breadcrumbs";
import PageLoading from "../../shared/PageLoading";
import MobileNav from "./MobileNav";
import { renderMenuItems } from "../../utils/route.utils";
import { useTranslation } from "react-i18next";
import { useCrumbs } from "../../hooks/useCrumbs";
import { getAppType, isAuthorized } from "../../utils/authority";
import { useConfig } from "../../hooks";
import { getMenuConfig } from "../../config/menuConfig";
import { ChatProvider } from "../../contexts/ChatContext";
import ChatLauncher from "../../components/GlobalChat/ChatLauncher";
import ChatWidget from "../../components/GlobalChat/ChatWidget";
import { TutorialOrchestrator } from "../../features/tutorials";
import { HelpHubModal } from "../../features/help-hub";

export default function DashboardLayout({ hideMenu }) {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  const { t } = useTranslation();
  const { navOpen, setNavOpen } = useContext(LayoutContext);
  const windowSize = useWindowSize();
  const { loading, user } = useAuth();
  const crumbs = useCrumbs();
  const { configs, appType } = useConfig();
  const location = useLocation();

  const isLoading = loading || !appType;

  const featureFlags = configs?.featureFlags || {};
  const menuMap = getMenuConfig(t)
    .filter((item) => {
      return item.appTypes && appType ? item.appTypes.includes(appType) : true;
    })
    .filter((item) => {
      if (!item.featureKey) return true;
      const val = featureFlags[item.featureKey];
      // Backwards compatible default: allow if missing
      if (val === undefined) return true;
      return Boolean(val);
    });

  const menuItems = renderMenuItems(menuMap, user?.authority);

  const breakPoint =
    windowSize.breakpoint === "sm" || windowSize.breakpoint === "xs";

  const bodyWrapperClasses = classNames(
    "flex flex-col w-full duration-200 mb-5 bg-gray-50",
    { "md:pl-20": !hideMenu && !navOpen },
    { "md:pl-[288px]": !hideMenu && navOpen }
  );

  const headFootTransition = {
    "md:pl-20": !hideMenu && !navOpen,
    "md:pl-60": !hideMenu && navOpen,
  };

  const rightWrapper = classNames(
    "flex flex-col h-screen duration-300 transition-all",
    "overflow-hidden md:overflow-visible",
    headFootTransition
  );

  const footerClasses = classNames(
    "duration-200 h-10 bg-gray-100 p-4 flex items-center justify-end",
    { "md:pl-20": !hideMenu && !navOpen, "md:pl-[288px]": !hideMenu && navOpen }
  );

  /**
   * SidebarContainer-style height calculation:
   * - Header + Breadcrumbs stay pinned (non-scrolling)
   * - Only the page content (Outlet) scrolls
   * - The body should not scroll (prevents "white space" scroll)
   */
  const topRef = useRef(null);
  const [topHeight, setTopHeight] = useState(0);
  const contentRef = useRef(null);

  useEffect(() => {
    // When the layout first loads, we may render <PageLoading /> before the
    // header/breadcrumbs mount. Re-run when `isLoading` flips false.
    if (isLoading) return;
    if (!topRef.current) return;

    const el = topRef.current;
    const update = () => {
      // Use bounding rect + ceil to avoid 1px rounding overflow that can
      // introduce a tiny extra scrollbar.
      const rect = el?.getBoundingClientRect?.();
      const height = rect?.height ?? el?.offsetHeight ?? 0;
      setTopHeight(Math.ceil(height));
    };

    update();
    const raf = requestAnimationFrame(update);
    const t = setTimeout(update, 50);

    // Keep height correct if header/breadcrumbs change (badges, translations, etc.)
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      ro?.disconnect?.();
    };
  }, [isLoading, windowSize?.size?.height, windowSize?.size?.width]);

  const contentHeight =
    typeof windowSize?.size?.height === "number"
      ? Math.max(windowSize.size.height - topHeight, 0)
      : undefined;

  /**
   * Debugging helper (opt-in):
   * Add `?debugLayout=1` to the URL to print layout measurements and identify
   * which element is actually scrolling.
   */
  const debugLayout =
    typeof window !== "undefined" &&
    new URLSearchParams(location.search).get("debugLayout") === "1";

  useEffect(() => {
    if (!debugLayout) return;
    let cancelled = false;
    let attempts = 0;
    let cleanupScrollListeners = null;

    const logOnceReady = () => {
      if (cancelled) return;

      const contentEl = contentRef.current;
      const topEl = topRef.current;
      const rootEl =
        contentEl?.closest?.('[data-layout="dashboard-root"]') ||
        document.querySelector?.('[data-layout="dashboard-root"]');

      // If we haven't mounted the dashboard layout yet (still on loading),
      // retry a few times.
      if (!contentEl || !topEl || !rootEl) {
        if (attempts < 15) {
          attempts += 1;
          setTimeout(logOnceReady, 100);
        }
        return;
      }

      const readEl = (el) => {
        if (!el) return null;
        const r = el.getBoundingClientRect?.();
        const style = window.getComputedStyle(el);
        return {
          tag: el.tagName,
          className: el.className,
          clientHeight: el.clientHeight,
          scrollHeight: el.scrollHeight,
          offsetHeight: el.offsetHeight,
          rectHeight: r ? Math.round(r.height) : null,
          overflowY: style.overflowY,
          overflowX: style.overflowX,
          scrollTop: el.scrollTop,
          scrollLeft: el.scrollLeft,
        };
      };

      const body = document.body;
      const docEl = document.documentElement;

      console.groupCollapsed(
        `[DashboardLayout debug] ${location.pathname}${location.search}`
      );
      console.table({
        windowInnerHeight: window.innerHeight,
        hookWindowHeight: windowSize?.size?.height,
        topHeight,
        contentHeight,
        bodyScrollTop: body.scrollTop,
        docScrollTop: docEl.scrollTop,
        bodyScrollHeight: body.scrollHeight,
        docScrollHeight: docEl.scrollHeight,
      });
      console.log("root:", readEl(rootEl));
      console.log("top:", readEl(topEl));
      console.log("content(main):", readEl(contentEl));
      console.groupEnd();

      // Identify what is actually scrolling.
      const onWindowScroll = () => {
        console.log("[DashboardLayout debug] window scroll", {
          docScrollTop: document.documentElement.scrollTop,
          bodyScrollTop: document.body.scrollTop,
        });
      };
      const onContentScroll = () => {
        console.log("[DashboardLayout debug] content scroll", {
          scrollTop: contentEl.scrollTop,
          scrollHeight: contentEl.scrollHeight,
          clientHeight: contentEl.clientHeight,
        });
      };

      window.addEventListener("scroll", onWindowScroll, { passive: true });
      contentEl.addEventListener("scroll", onContentScroll, { passive: true });

      cleanupScrollListeners = () => {
        window.removeEventListener("scroll", onWindowScroll);
        contentEl.removeEventListener("scroll", onContentScroll);
      };
    };

    logOnceReady();

    return () => {
      cancelled = true;
      cleanupScrollListeners?.();
    };
  }, [
    debugLayout,
    location.pathname,
    location.search,
    isLoading,
    windowSize?.size?.height,
    windowSize?.size?.width,
    topHeight,
    contentHeight,
  ]);

  useEffect(() => {
    const handleTutorialStepChange = (event) => {
      const stepId = event?.detail?.stepId;
      if (stepId === "sidebar" && !navOpen) {
        // Ensure users can see and understand sidebar layout during the tour.
        setNavOpen(true);
      }
    };

    window.addEventListener("attune:tutorial-step-change", handleTutorialStepChange);
    return () => {
      window.removeEventListener(
        "attune:tutorial-step-change",
        handleTutorialStepChange
      );
    };
  }, [navOpen, setNavOpen]);

  return isLoading ? (
    <PageLoading />
  ) : (
    <ChatProvider>
      <div
        // style={wrapperStyle}
        className="w-full h-screen overflow-hidden"
        data-layout="dashboard-root"
        style={{
          // Prefer exact px height to avoid 1px rounding scroll,
          // but fall back to 100vh via `h-screen` if anything is missing.
          ...(typeof windowSize?.size?.height === "number"
            ? { height: `${windowSize.size.height}px` }
            : {}),
        }}
      >
        {!hideMenu && (
          <>
            <div className="hidden md:block">
              <Sidebar
                className="hidden md:block"
                loading={loading}
                menu={menuItems}
              />
            </div>
            <MobileNav
              breakPoint={breakPoint}
              noBorderHeader={true}
              position="left"
              menu={menuItems}
              setNavOpen={setNavOpen}
              navOpen={navOpen}
            />
          </>
        )}
        <div className={rightWrapper}>
          <div ref={topRef}>
            <LayoutHeader
              user={user}
              openDrawer={() => setDrawerVisible(true)}
            />
            <div className="border-b border-gray-200">
              <Breadcrumbs
                data={crumbs.crumbs}
                loading={crumbs.loading}
                rightContent={crumbs.rightContent}
              />
            </div>
          </div>
          <main
            ref={contentRef}
            className="overflow-y-auto"
            style={
              contentHeight !== undefined
                ? {
                    height: `${contentHeight}px`,
                    minHeight: `${contentHeight}px`,
                  }
                : undefined
            }
          >
            <Outlet />
          </main>
          {/* <footer className="py-2 bg-gray-100 border-t text-center text-white">
            Footer
          </footer> */}
        </div>
      </div>
      <ChatWidget
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        onOpen={() => setChatOpen(true)}
        onUnreadCountChange={setChatUnreadCount}
      />
      <TutorialOrchestrator />
      <HelpHubModal />
      <ChatLauncher unreadCount={chatUnreadCount} onClick={() => setChatOpen((v) => !v)} />
      {/* <Drawer
              position="right"
              title="Site Settings"
              body={<p>Site Settings</p>}
              visible={drawerVisible}
              setVisible={setDrawerVisible}
              onClose={() => setDrawerVisible(false)}
            /> */}
    </ChatProvider>
  );
  //   }}
  // </SidebarContainer>
  // );
}

// const data = (
//   <>
//     <div className="flex">
//       {!hideMenu && (
//         <>
//           <Sidebar className="hidden md:block" menu={menuItems} />
//           <MobileNav
//             breakPoint={breakPoint}
//             noBorderHeader={true}
//             position="left"
//             menu={menuItems}
//             setNavOpen={setNavOpen}
//             navOpen={navOpen}
//           />
//         </>
//       )}
//       <div className={bodyWrapperClasses}>
//         <LayoutHeader user={user} openDrawer={() => setDrawerVisible(true)} />
//         <Breadcrumbs data={breadcrumbs} />
//         <div className="px-3 pb-3 h-auto">
//           <Outlet />
//         </div>
//       </div>
//     </div>
//     <Drawer
//       position="right"
//       title="Site Settings"
//       body={<p>Site Settings</p>}
//       visible={drawerVisible}
//       setVisible={setDrawerVisible}
//       onClose={() => setDrawerVisible(false)}
//     />
//   </>
// );
